import { Box, Typography } from '@mui/material';
import { MultiSelectQuestion } from 'src/suitability/engine/components/multi-select-question';
import { PercentQuestion } from 'src/suitability/engine/components/percent-question';
import { RadioQuestion } from 'src/suitability/engine/components/radio-question';
import type { TestStepProps } from 'src/suitability/engine/types';
import {
  S2_MAX_PERCENTAGE,
  S2_MIN_PERCENTAGE,
  type LiftAnswers,
  type LiftSustainabilityAnswers,
} from './answers';
import {
  toMultiSelectOptions,
  toRadioOptions,
  useLiftContent,
} from './content';
import {
  isSelfDetermined,
  opensAdverseImpact,
  opensSfdr,
  opensTaxonomy,
  pruneSustainability,
} from './sustainability';

/** Clave de multiselección → lista de órdenes, que es como viaja al motor. */
const toOrders = (selection: Record<string, boolean>): number[] =>
  Object.entries(selection)
    .filter(([, isSelected]) => isSelected)
    .map(([order]) => Number(order))
    .sort((a, b) => a - b);

const toSelection = (orders: number[] | undefined): Record<string, boolean> =>
  Object.fromEntries((orders ?? []).map((order) => [String(order), true]));

/**
 * Bloque de preferencias de sostenibilidad (S1–S6): el único opcional, el único que no
 * puntúa y el único con árbol condicional.
 *
 * S1 lo gobierna todo: neutro o delegar cierra el bloque ahí. Sólo "quiero determinar
 * tipología y proporción" abre S2 y S3, y cada característica marcada en S3 abre su
 * condicional — taxonomía → S4, SFDR → S5, PIAS → S6.
 *
 * Cada cambio pasa por `pruneSustainability`, así que las respuestas de una rama que
 * se cierra desaparecen en el momento en vez de quedarse invisibles esperando a que el
 * motor las pode. Lo que se ve en pantalla y lo que se guarda son lo mismo.
 */
export const StepSustainability = ({
  answers,
  onUpdate,
}: TestStepProps<LiftAnswers>) => {
  const content = useLiftContent();
  const step = content.step('sustainability');
  const value = answers.sustainability;

  // `onUpdate` hace merge SUPERFICIAL: parchear `sustainability` reemplaza el objeto
  // entero, así que hay que esparcir lo que ya había antes de podar.
  const update = (patch: Partial<LiftSustainabilityAnswers>) =>
    onUpdate({ sustainability: pruneSustainability({ ...value, ...patch }) });

  const question = (key: string) => content.question(key);

  /**
   * El asterisco significa una sola cosa en el wizard: sin esto no avanzas. S1 NO lo
   * lleva —dejar el bloque en blanco cierra el test igual—, y ponérselo empujaría al
   * cliente a contestar "Neutro" para desbloquear algo que no está bloqueado; y en
   * blanco y Neutro son estados distintos en el registro. S2, S4 y S5 sí, que en
   * cuanto se ven son obligatorias.
   */
  const single = (key: 's1' | 's4' | 's5') => {
    const definition = question(key);

    return (
      <RadioQuestion
        question={definition.prompt}
        options={toRadioOptions(key, definition)}
        value={value?.[key] === undefined ? undefined : String(value[key])}
        onChange={(order) => update({ [key]: Number(order) })}
        required={key !== 's1'}
      />
    );
  };

  /**
   * `MultiSelectQuestion` no pinta enunciado —así lo usa también la P5 de Wio—, así que
   * lo pone el paso. Con su asterisco: S3 y S6 son obligatorias en cuanto se ven y, sin
   * marca, dejaban el botón bloqueado sin nada en pantalla que dijera por qué.
   */
  const multiple = (key: 's3' | 's6') => {
    const definition = question(key);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="textMd" sx={{ fontWeight: 600, mb: 1.5 }}>
          {definition.prompt}
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        </Typography>
        <MultiSelectQuestion
          options={toMultiSelectOptions(definition)}
          value={toSelection(value?.[key])}
          onChange={(selection) => update({ [key]: toOrders(selection) })}
        />
      </Box>
    );
  };

  const percentage = question('s2');

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      {step.intro && (
        <Typography
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
        >
          {step.intro}
        </Typography>
      )}

      {single('s1')}

      {isSelfDetermined(value) && (
        <>
          <PercentQuestion
            question={percentage.prompt}
            suffix={percentage.suffix}
            value={value?.s2}
            onChange={(s2) => update({ s2 })}
            min={S2_MIN_PERCENTAGE}
            max={S2_MAX_PERCENTAGE}
            required
          />
          {multiple('s3')}
          {opensTaxonomy(value) && single('s4')}
          {opensSfdr(value) && single('s5')}
          {opensAdverseImpact(value) && multiple('s6')}
        </>
      )}
    </Box>
  );
};